

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Grid, Icon, Snackbar, Alert } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewGuideTripNoteModal from 'components/NewGuideTripNoteModal';
import { formatDate } from 'utils/DateTimeConversion';

const GuideTripNotes = () => {
    const [notes, setNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('guide_trip_notes');

    useEffect(() => {
        fetchNotes();
    }, []);

    const fetchNotes = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/guide-trip-notes/');
            setNotes(response.data);
        } catch (error) {
            console.error('Error fetching guide trip notes:', error);
            setSnackbar({ open: true, message: 'Error loading guide trip notes', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveNote = async (payload) => {
        try {
            await axiosInstance.post('/guide-trip-notes/', payload);
            setSnackbar({ open: true, message: 'Guide Trip Note created successfully', severity: 'success' });
            fetchNotes();
        } catch (error) {
            console.error('Error saving guide trip note:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/guide-trip-note/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Date",
            accessor: "date",
            width: "12%",
            Cell: ({ value }) => value ? formatDate(value) : '-'
        },
        {
            Header: "Guide",
            accessor: "guide_id",
            width: "15%",
            Cell: ({ row }) => row.original.guide?.name || row.original.guide_id || '-'
        },
        {
            Header: "Trip",
            accessor: "trip_id",
            width: "15%",
            Cell: ({ row }) => row.original.trip?.name || row.original.trip_id || '-'
        },
        {
            Header: "Did Day Run According to Plan?",
            accessor: "did_day_run_according_to_plan",
            width: "20%"
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "12%",
            Cell: ({ value }) => value ? formatDate(value) : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: notes,
    }), [notes]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Guide Trip Notes</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Guide Trip Note
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : notes.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                        onRowClick={handleRowClick}
                                    />
                                ) : (
                                    <EmptyState moduleName="Guide Trip Note" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewGuideTripNoteModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveNote}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default GuideTripNotes;

